/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { Flex, Button, Text } from '@chakra-ui/react';

import {
  DateInputWithConfirm,
  CheckboxOptionsWithConfirm,
  UploadActions,
  UploadButtonSection,
  UploadExample,
  UploadComplete as UploadCompleteSection,
  AcknowledgeBox,
  getStringFromDate,
  ModalColumns,
  RequirementsRemedy,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import sponsorshipLetterExample from '../../../../../assets/images/sample-sponsorship-letter.jpg';
import { ICheckbox, IDate, OrganisationDoc } from '../../../../../types';
import { isValidDate } from '../../../../../utilities/validation';

import getHighlightedErrorSections from './getHighlightedErrorSections';
import Requirements from './requirements';

interface IProps {
  sectionTitle?: string;
  fileNames: string[];
  document: OrganisationDoc;
  onContinue: () => void;
  onUpload: (metaData: any) => void;
  onClose: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string, infoOnlyRequirementsOption?: string, metadata?: any) => void;
}
interface IState {
  infoOnlyRequirementsOption?: string;
  questionsComplete: boolean;
  showUpload: boolean;
  highlightedErrorSections: string[];
  hasErrors: boolean;
  hasLetter?: boolean;
  hasLetterError: string;
  hasOfficialLetterhead?: boolean;
  hasOfficialLetterheadError: string;
  nameAndLogoVisible?: boolean;
  nameAndLogoVisibleError: string;
  officiallySigned?: boolean;
  officiallySignedError: string;
  hasDate?: boolean;
  hasDateError: string;
  dateOnLetter: IDate;
  dateOnLetterError: string;
  selectedTextBodyReq: string[];
  selectedTextBodyReqError: string;
  hasContactDetails?: boolean;
  hasContactDetailsError: string;
}
class OrganisationSponsorModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const {
      infoOnlyRequirementsOption,
      hasOfficialLetterhead,
      nameAndLogoVisible,
      officiallySigned,
      hasDate,
      dateOnLetter,
      selectedTextBodyReq,
      hasContactDetails
    } = props.document!;

    const questionsComplete = props.infoOnly
      ? infoOnlyRequirementsOption !== undefined
      : hasOfficialLetterhead !== undefined &&
        nameAndLogoVisible !== undefined &&
        officiallySigned !== undefined &&
        hasDate !== undefined &&
        ((hasDate === true && dateOnLetter !== '') ||
          (hasDate === false && (dateOnLetter === '' || dateOnLetter === undefined))) &&
        selectedTextBodyReq !== undefined &&
        hasContactDetails !== undefined;

    this.state = {
      infoOnlyRequirementsOption: infoOnlyRequirementsOption,
      questionsComplete: questionsComplete,
      showUpload: false,
      highlightedErrorSections: [],
      hasErrors: false,

      hasLetter: undefined,
      hasLetterError: '',
      hasOfficialLetterhead: undefined,
      hasOfficialLetterheadError: '',
      nameAndLogoVisible: undefined,
      nameAndLogoVisibleError: '',
      officiallySigned: undefined,
      officiallySignedError: '',
      hasDate: undefined,
      hasDateError: '',
      dateOnLetter: {
        DD: null,
        MM: null,
        YYYY: null
      },
      dateOnLetterError: '',
      selectedTextBodyReq: [],
      selectedTextBodyReqError: '',
      hasContactDetails: undefined,
      hasContactDetailsError: ''
    };
  }

  componentDidMount() {
    if (this.state.questionsComplete && this.props.document) {
      const highlightedErrorSections = getHighlightedErrorSections(this.props.document!);

      this.setState({
        highlightedErrorSections,
        hasErrors: this.props.document.isDocumentFixed ? false : highlightedErrorSections.length > 0
      });
    }
  }

  onQuestionButtonClick = (question: IQuestion, stateName: string) => {
    switch (stateName) {
      case 'hasDate':
        if (this.state[stateName] !== (question.label === 'Yes'))
          this.setState(prevState => ({
            ...prevState,
            [stateName]: question.label === 'Yes',
            dateOnLetter: { DD: null, MM: null, YYYY: null }
          }));
        break;
      case 'infoOnlyRequirementsOption':
        this.setState({
          infoOnlyRequirementsOption: question.label === 'Read a list of requirements' ? 'list' : 'checklist'
        });
        this.toggleAcknowledge('', false);
        break;
      default:
        this.setState(prevState => ({
          ...prevState,
          [stateName]: question.label === 'Yes'
        }));
    }
  };

  isFormValid = () => {
    const { hasOfficialLetterhead, nameAndLogoVisible, officiallySigned, hasDate, dateOnLetter, hasContactDetails } =
      this.state;

    const hasOfficialLetterheadError = hasOfficialLetterhead === undefined ? 'Please select an option' : '';
    const nameAndLogoVisibleError = nameAndLogoVisible === undefined ? 'Please select an option' : '';
    const officiallySignedError = officiallySigned === undefined ? 'Please select an option' : '';
    const hasDateError = hasDate === undefined ? 'Please select an option' : '';
    const dateOnLetterError =
      hasDate === true &&
      dateOnLetter &&
      (dateOnLetter.DD === null || dateOnLetter.MM === null || dateOnLetter.YYYY === null)
        ? 'Please fill in a date'
        : '';
    const hasContactDetailsError = hasContactDetails === undefined ? 'Please select an option' : '';

    if (
      hasOfficialLetterheadError ||
      nameAndLogoVisibleError ||
      officiallySignedError ||
      hasDateError ||
      dateOnLetterError ||
      hasContactDetailsError
    ) {
      this.setState({
        hasOfficialLetterheadError,
        nameAndLogoVisibleError,
        officiallySignedError,
        hasDateError,
        dateOnLetterError,
        hasContactDetailsError
      });
      return false;
    }
    return true;
  };

  onContinueClick = () => {
    const isFormValid = this.isFormValid();
    const highlightedErrorSections = getHighlightedErrorSections({
      ...this.state,
      dateOnLetter: getStringFromDate(this.state.dateOnLetter)
    });
    // Allow user to upload document, but show warnings once completed
    this.setState(
      {
        highlightedErrorSections,
        hasErrors: !isFormValid,
        questionsComplete: isFormValid
      },
      () => {
        if (isFormValid && !this.props.infoOnly) {
          this.onUpload();
        }
        if (this.props.infoOnly) {
          setTimeout(() => window.scrollTo(0, 0), 150); // timeout added for Chrome issue
          this.props.onAcknowledgeToggle('NOT_ACKNOWLEDGED', this.state.infoOnlyRequirementsOption, this.getMetaData());
        }
      }
    );
  };

  onFixUpload = () => {
    this.setState(
      {
        highlightedErrorSections: [],
        hasErrors: false
      },
      () => this.onUpload(true)
    );
  };

  onUploadAfterApproved = () => {
    const metaData = {
      hasOfficialLetterhead: true,
      nameAndLogoVisible: true,
      officiallySigned: true,
      hasDate: true,
      dateOnLetter: undefined,
      selectedTextBodyReq: undefined,
      hasContactDetails: true
    };
    this.props.onUpload(metaData);
  };

  getMetaData = (isFix?: boolean) => {
    const {
      hasOfficialLetterhead,
      nameAndLogoVisible,
      officiallySigned,
      hasDate,
      dateOnLetter,
      selectedTextBodyReq,
      hasContactDetails,
      highlightedErrorSections
    } = this.state;

    const dateOnLetterStr = isFix ? undefined : getStringFromDate(dateOnLetter);

    return {
      hasOfficialLetterhead,
      nameAndLogoVisible,
      officiallySigned,
      hasDate,
      dateOnLetter: dateOnLetterStr,
      selectedTextBodyReq: isFix ? undefined : selectedTextBodyReq,
      hasContactDetails,
      shouldShowRequirements: highlightedErrorSections.length > 0,
      isDocumentFixed: !!isFix
    };
  };

  onUpload = (isFix?: boolean) => {
    this.props.onUpload(this.getMetaData(isFix));
  };

  getSelectedTextBodyReqCheckboxes = (): ICheckbox[] => {
    return [
      {
        id: 'fullName',
        label: 'Your full name as per your passport',
        isTicked: !!(this.state.selectedTextBodyReq && this.state.selectedTextBodyReq.includes('fullName')),
        readOnly: false
      },
      {
        id: 'orgName',
        label: 'The name of the organisation sponsoring you',
        isTicked: !!(this.state.selectedTextBodyReq && this.state.selectedTextBodyReq.includes('orgName')),
        readOnly: false
      },
      {
        id: 'courseName',
        label: 'The name of your course',
        isTicked: !!(this.state.selectedTextBodyReq && this.state.selectedTextBodyReq.includes('courseName')),
        readOnly: false
      },
      {
        id: 'contributionAmount',
        label:
          'The amount in pounds or percentage covered or a statement that confirms that ALL living costs and fees are covered',
        isTicked: !!(this.state.selectedTextBodyReq && this.state.selectedTextBodyReq.includes('contributionAmount')),
        readOnly: false
      },
      {
        id: 'periodCovered',
        label: 'The period that the sponsorship will cover',
        isTicked: !!(this.state.selectedTextBodyReq && this.state.selectedTextBodyReq.includes('periodCovered')),
        readOnly: false
      }
    ];
  };

  onSelectedTextBodyReqChange = (id: string, isTicked: boolean) => {
    if (!isTicked) {
      // remove from list
      this.setState({
        selectedTextBodyReq: this.state.selectedTextBodyReq.filter((reqId: string) => reqId !== id)
      });
    } else {
      // add to list
      this.setState({
        selectedTextBodyReq: [...this.state.selectedTextBodyReq, id]
      });
    }
  };

  onBlurDateOnLetter = (event: React.FormEvent<HTMLInputElement>) => {
    const { DD, MM, YYYY } = this.state.dateOnLetter;

    if (DD && MM && YYYY) {
      this.setState(state => {
        if (
          (state.dateOnLetter &&
            (state.dateOnLetter.DD === null || state.dateOnLetter.MM === null || state.dateOnLetter.YYYY === null)) ||
          !isValidDate(state.dateOnLetter)
        ) {
          return {
            dateOnLetterError: 'Please ensure the date is correct'
          };
        } else {
          return null;
        }
      });
    }
  };

  handleOnClose = () => {
    if (this.props.docStatus === 'INFO_ONLY') {
      this.props.onAcknowledgeToggle('NOT_ACKNOWLEDGED', this.state.infoOnlyRequirementsOption);
    }
    this.props.onClose();
  };

  toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if (
      (value === true && this.props.docStatus !== 'ACKNOWLEDGED') ||
      (value === false && this.props.docStatus === 'ACKNOWLEDGED')
    ) {
      this.props.onAcknowledgeToggle(
        value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED',
        this.state.infoOnlyRequirementsOption
      );
    }
  };

  renderQuestions = () => {
    const {
      hasOfficialLetterhead,
      hasOfficialLetterheadError,
      nameAndLogoVisible,
      nameAndLogoVisibleError,
      officiallySigned,
      officiallySignedError,
      hasDate,
      hasDateError,
      dateOnLetter,
      dateOnLetterError,
      hasContactDetails,
      hasContactDetailsError
    } = this.state;

    return (
      <>
        <RadioOptionsWithConfirm
          label="Is your letter printed on official letterhead?"
          errorText={hasOfficialLetterheadError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasOfficialLetterhead === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasOfficialLetterhead === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasOfficialLetterhead')}
        />

        <RadioOptionsWithConfirm
          label="Is the sponsoring organisation's name and logo visible on the letterhead?"
          errorText={nameAndLogoVisibleError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: nameAndLogoVisible === true
            },
            {
              char: 'N',
              label: 'No',
              selected: nameAndLogoVisible === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'nameAndLogoVisible')}
        />

        <RadioOptionsWithConfirm
          label="Is it officially stamped/sealed AND signed?"
          errorText={officiallySignedError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: officiallySigned === true
            },
            {
              char: 'N',
              label: 'No',
              selected: officiallySigned === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'officiallySigned')}
        />

        <RadioOptionsWithConfirm
          label="Is there a date on the letter?"
          errorText={hasDateError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasDate === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasDate === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasDate')}
        />

        {hasDate === true && (
          <DateInputWithConfirm
            value={dateOnLetter}
            onBlur={this.onBlurDateOnLetter}
            label={'What is the date on the letter?'}
            errorText={dateOnLetterError}
            onChangeDD={(evt: any) =>
              this.setState({
                dateOnLetter: {
                  ...dateOnLetter,
                  DD: evt.target.value
                }
              })
            }
            onChangeMM={(evt: any) =>
              this.setState({
                dateOnLetter: {
                  ...dateOnLetter,
                  MM: evt.target.value
                }
              })
            }
            onChangeYY={(evt: any) =>
              this.setState({
                dateOnLetter: {
                  ...dateOnLetter,
                  YYYY: evt.target.value
                }
              })
            }
          />
        )}

        <CheckboxOptionsWithConfirm
          id="body-of-text-inclusions"
          labelText="Does the body text of the letter include:"
          subText="Tick each item as necessary"
          errorText={this.state.selectedTextBodyReqError}
          checkboxes={this.getSelectedTextBodyReqCheckboxes()}
          onChange={this.onSelectedTextBodyReqChange}
        />

        <RadioOptionsWithConfirm
          label="Are contact details for your sponsor included?"
          errorText={hasContactDetailsError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasContactDetails === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasContactDetails === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasContactDetails')}
        />

        <Flex justifyContent="end">
          <Button onClick={this.onContinueClick} variant="solid" size="lg">
            Continue
          </Button>
        </Flex>
      </>
    );
  };

  renderContent = () => {
    const {
      infoOnlyRequirementsOption,
      questionsComplete,
      hasErrors,
      highlightedErrorSections,
      hasLetter,
      hasLetterError
    } = this.state;
    const { docStatus, infoOnly, docKey, onClose, onContinue, sectionTitle, fileNames } = this.props;

    return (
      <>
        {infoOnly ? (
          <>
            <Text>
              During your visa application you may be required to provide a letter from your sponsor stating the details
              of your sponsorship.
            </Text>

            <RadioOptionsWithConfirm
              id="infoOnlyRequiremnentsOption"
              label="There are two ways you can check the requirements of this document"
              questions={[
                {
                  char: '1',
                  label: 'Read a list of requirements',
                  selected: infoOnlyRequirementsOption === 'list'
                },
                {
                  char: '2',
                  label: 'Use our online document checklist',
                  selected: infoOnlyRequirementsOption === 'checklist'
                }
              ]}
              onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'infoOnlyRequirementsOption')}
            />

            {infoOnlyRequirementsOption === 'list' ? (
              <>
                <Requirements uploadComplete={false} highlightedSections={[]} />

                <AcknowledgeBox
                  id={docKey}
                  blurb="Please confirm you have read and understand the above."
                  isTicked={docStatus === 'ACKNOWLEDGED'}
                  onChange={this.toggleAcknowledge}
                />
              </>
            ) : null}
            {infoOnlyRequirementsOption === 'checklist' ? (
              <>
                {!questionsComplete ? (
                  <>{this.renderQuestions()}</>
                ) : (
                  <>
                    {this.state.highlightedErrorSections.length ? (
                      <Requirements
                        uploadComplete={questionsComplete}
                        highlightedSections={this.state.highlightedErrorSections}
                      />
                    ) : (
                      <RequirementsRemedy isSatisfied>
                        <Text>
                          Based on the responses provided by you, your document appears to meet requirements at this
                          point in time.
                        </Text>

                        <Text>
                          If you would like to check the requirements of this document manually, you can select "read a
                          list of requirements" in the above options.
                        </Text>
                      </RequirementsRemedy>
                    )}

                    <AcknowledgeBox
                      id={docKey}
                      blurb="Please confirm you have read and understand the above."
                      isTicked={docStatus === 'ACKNOWLEDGED'}
                      onChange={this.toggleAcknowledge}
                    />
                  </>
                )}
              </>
            ) : null}
            {infoOnlyRequirementsOption === 'list' ||
            (infoOnlyRequirementsOption === 'checklist' && questionsComplete) ? (
              <UploadActions complete={!hasErrors} onContinue={this.handleOnClose} label="Return to document upload" />
            ) : null}
          </>
        ) : (
          <>
            {!questionsComplete && docStatus === 'AWAITING' ? (
              <>
                <RadioOptionsWithConfirm
                  id="organisationSponsorModal"
                  label="Have you got your letter yet?"
                  errorText={hasLetterError}
                  questions={[
                    {
                      char: 'Y',
                      label: 'Yes',
                      selected: hasLetter === true
                    },
                    {
                      char: 'N',
                      label: 'No',
                      selected: hasLetter === false
                    }
                  ]}
                  isInline={true}
                  onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasLetter')}
                />
              </>
            ) : (
              docStatus === 'AWAITING' && (
                <>
                  <UploadButtonSection onUpload={this.onUpload}>
                    Awaiting upload of <b>{sectionTitle}</b>
                  </UploadButtonSection>

                  <UploadActions complete={false} onContinue={onContinue} />
                </>
              )
            )}

            {hasLetter === true && !questionsComplete && docStatus === 'AWAITING' ? (
              <>{this.renderQuestions()}</>
            ) : null}

            {(hasLetter === false && !questionsComplete) || (docStatus !== 'AWAITING' && hasErrors) ? (
              <>
                <Requirements
                  uploadComplete={docStatus !== 'AWAITING'}
                  highlightedSections={highlightedErrorSections}
                />

                {hasErrors && (
                  <>
                    <RequirementsRemedy>
                      <Text>
                        You will need to <b>upload a new sponsorship letter</b> after you have fixed the issues with the
                        current letter.
                      </Text>
                    </RequirementsRemedy>

                    <UploadButtonSection onUpload={this.onFixUpload} buttonLabelText={'Upload new sponsorship letter'}>
                      Awaiting upload of <b>new sponsorship letter</b>
                    </UploadButtonSection>
                  </>
                )}

                <UploadActions complete={!hasErrors} onContinue={onClose} label="Return to document upload" />
              </>
            ) : docStatus !== 'AWAITING' && hasErrors === false && questionsComplete ? (
              <UploadCompleteSection
                fileNames={fileNames}
                onUpload={this.onFixUpload}
                onContinue={this.props.onClose}
              />
            ) : docStatus === 'APPROVED' ? (
              <UploadCompleteSection
                fileNames={fileNames}
                onUpload={this.onUploadAfterApproved}
                onContinue={this.props.onClose}
              />
            ) : docStatus === 'UPLOADED_OK' ? (
              <>
                <UploadButtonSection onUpload={this.onFixUpload} buttonLabelText={'Add additional files'}>
                  <b>Uploaded by University</b>
                </UploadButtonSection>

                <UploadActions complete={true} onContinue={onClose} label="Finish" />
              </>
            ) : null}
          </>
        )}
      </>
    );
  };

  render() {
    const { sectionTitle } = this.props;
    return (
      <ModalColumns
        id={'organisationSponsor'}
        title={sectionTitle}
        onClose={this.handleOnClose}
        supportingColumn={<UploadExample altText="Sponsorship letter example" url={sponsorshipLetterExample} />}
      >
        {this.renderContent()}
      </ModalColumns>
    );
  }
}

export default OrganisationSponsorModal;
